import PartnerPortfolioUebersicht from "./pages/PartnerPortfolioUebersicht";
import {
  FluentProvider,
  RendererProvider,
  webLightTheme,
  makeStyles,
} from "@fluentui/react-components";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router";
import "./App.css";
import NavigationLayout from "./components/NavigationLayout";
import { WindowManagerProvider } from "./components/windowManager/WindowManagerContext";
import { hauptRenderer } from "./components/windowManager/browserFenster/hauptRenderer";
import { OfficeOpenMenuProvider } from "./components/documents/OfficeOpenMenuProvider";
import { useDisableBrowserAutofill } from "./hooks/useDisableBrowserAutofill";
import { ContactDetailsForm } from "./components/ContactDetails";
import { SchadenForm } from "./components/SchadenForm";
import { RisikoForm } from "./components/RisikoForm";
import { RisikoImmoForm } from "./components/RisikoImmoForm";
import RisikenTabelleDataGrid from "./components/RisikenTabelleDataGrid";
// Redesign-Views (Refined Classic) — ersetzen die Live-Routen für Tabelle &
// Detail. Logik/Datenanbindung folgen später; aktuell statisches Redesign.
// Die alte ContactTable-Komponente bleibt als Datei erhalten (u.a. von
// RisikoImmoForm genutzt) und wird später für die Logik-Migration herangezogen.
import ContactTableRedesign from "./components/redesign/ContactTableRedesign";
import VertraegeTabelleRedesign from "./components/redesign/VertraegeTabelleRedesign";
import RisikenTabelleRedesign from "./components/redesign/RisikenTabelleRedesign";
import ContactDetailsRedesign from "./components/redesign/ContactDetailsRedesign";
import ContractTable from "./components/ContractTable";
import DamagesTable from "./components/DamagesTable";
import AlteKundenaktenSeite from "./components/alteKundenakten/AlteKundenaktenSeite";
import AlteKundenaktenDetail from "./components/alteKundenakten/AlteKundenaktenDetail";
import { DamagesForm } from "./components/DamagesForm";
import Dashboard from "./components/dashboard/DashboardHome";
import CalendarView from "./components/calendar/CalendarView";
import TerminNotizenPage from "./components/calendar/TerminNotizenPage";
import { InMemoryCache, ApolloClient, HttpLink } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import ContactMultiStepForm from "./components/ContactMultiStepForm";
import ContactNewPage from "./components/ContactNewPage";
import CompanyContactTable from "./pages/CompanyContactTable";
import { SchriftverkehrAnsicht } from "./components/dvagPost/SchriftverkehrAnsicht";
import { VorgaengeMo } from "./components/VorgaengeMo";
import VorgangDetailPageV4 from "./pages/VorgangDetailPageV4";
import VorgangAltpfadRedirect from "./pages/VorgangAltpfadRedirect";
import TodoRedirectPage from "./pages/TodoRedirectPage";
import TelephonyPage from "./components/telephony/TelephonyPage";
import TelephonyExtensionsPage from "./pages/settings/TelephonyExtensionsPage";
import TicketsPage from "./components/tickets/TicketsPage";
import { TelephonyProvider } from "./context/TelephonyContext";
import { RealtimeProvider } from "./context/RealtimeContext";
import { ChatSocketProvider } from "./context/ChatSocketContext";
import { ChatProvider } from "./context/ChatContext";
import { ReferenceDataProvider } from "./context/ReferenceDataContext";
import { AppSettingsProvider } from "./context/AppSettingsContext";
import CallWidget from "./components/telephony/CallWidget";
import ActiveCallBar from "./components/telephony/ActiveCallBar";
import NachAnrufKarte from "./components/telephony/NachAnrufKarte";
import SchnellwahlModal from "./components/telephony/SchnellwahlModal";
import NewCompanyContactPage from "./pages/NewCompanyContactPage";
import NewPrivateContactPage from "./pages/NewPrivateContactPage";
import NewContactPage from "./pages/NewContactPage";
import ConvertContactPage from "./pages/ConvertContactPage";
import NewRiskRoute from "./pages/NewRiskRoute";
import { apiUrl } from "./utils/api";
import PublicCustomerForm from "./pages/PublicCustomerForm";

/**
 * Nur lokal: die Vorschau-Seite fuer das Inhaltsverzeichnis. Der dynamische
 * Import steht bewusst hinter `import.meta.env.DEV` — im Produktions-Build
 * bleibt hier `null` und der Chunk wird gar nicht erst erzeugt.
 */
const VorschauInhaltsverzeichnis = import.meta.env.DEV
  ? React.lazy(() => import("./pages/VorschauInhaltsverzeichnis"))
  : null;
import PublicSchadenmeldungBig from "./pages/PublicSchadenmeldungBig";
import RechnungenTabelle from "./components/redesign/RechnungenTabelle";
import RechnungDetailPage from "./pages/RechnungDetailPage";
import Schadenmappe from "./pages/Schadenmappe";
import SchadenmappePage from "./pages/schadenmappe/SchadenmappePage";
import PublicCustomerFormHub from "./pages/PublicCustomerFormHub";
import PublicVollmachtUnterschreiben from "./pages/PublicVollmachtUnterschreiben";
import PublicCustomerCheckAddressForm from "./pages/PublicCustomerCheckAddressForm";
import PublicCustomerRisikoanalyseForm from "./pages/PublicCustomerRisikoanalyseForm";
import PublicCustomerRisikoanalyseKurzForm from "./pages/PublicCustomerRisikoanalyseKurzForm";
import PublicCustomerRisikoanalyseKurzScrollForm from "./pages/PublicCustomerRisikoanalyseKurzScrollForm";
import InternalRisikoanalyseFormPage from "./pages/InternalRisikoanalyseFormPage";
import AnalysemodusAuswertungRedirect from "./pages/AnalysemodusAuswertungRedirect";
import BeratermodusFormPage from "./pages/BeratermodusFormPage";
import RisikoKfzStepFormPublic from "./pages/RisikoKfzStepForm";
import RisikoKfzLegacyPage from "./pages/RisikoKfzLegacyPage";
import RisikoKennzahlenStepFormPublic from "./pages/RisikoKennzahlenStepForm";
import RisikoKennzahlenLegacyPage from "./pages/RisikoKennzahlenLegacyPage";
import KennzahlenSectionStepForm from "./pages/KennzahlenSectionStepForm";
import RisikoImmobilieStepFormPublic from "./pages/RisikoImmobilieStepForm";
import RisikoImmobilieLegacyPage from "./pages/RisikoImmobilieLegacyPage";
import RisikoanalyseDetailFormPublic from "./pages/RisikoanalyseDetailFormPublic";
import RiskDetailDispatcher from "./pages/RiskDetailDispatcher";
import ContractDetailDispatcher from "./pages/ContractDetailDispatcher";
import ImmobilieContractCreatePage from "./pages/ImmobilieContractCreatePage";
import KfzContractCreatePage from "./pages/KfzContractCreatePage";
import NewContractPage from "./pages/NewContractPage";
import TransportContractCreatePage from "./pages/TransportContractCreatePage";
import ErtragsausfallContractCreatePage from "./pages/ErtragsausfallContractCreatePage";
import RequireAuth from "./components/auth/RequireAuth";
import OfficeEditorPage from "./pages/OfficeEditorPage";
import PdfDokumentPage from "./pages/PdfDokumentPage";
import AccessDeniedPage from "./pages/AccessDeniedPage";
import ChatPage from "./pages/ChatPage";
import RisikoanalyseStepInternalPage from "./pages/RisikoanalyseStepInternalPage";
import PendingApprovalPage from "./pages/PendingApprovalPage";
import SettingsPage from "./pages/SettingsPage";
import ProfilePage from "./pages/ProfilePage";
import ImportPage from "./pages/ImportPage";
import ContactDuplicatesPage from "./pages/ContactDuplicatesPage";
import PapierkorbPage from "./pages/PapierkorbPage";
import ContactMergePage from "./pages/ContactMergePage";
import KundenverbindungMergePage from "./pages/KundenverbindungMergePage";
import DatenbereinigungPage from "./pages/DatenbereinigungPage";
import AnsprechpartnerCleanupPage from "./pages/AnsprechpartnerCleanupPage";
import HaushalteCleanupPage from "./pages/HaushalteCleanupPage";
import AltkundenCleanupPage from "./pages/AltkundenCleanupPage";
import UnternehmensstrukturenCleanupPage from "./pages/UnternehmensstrukturenCleanupPage";
import NamenszusatzCleanupPage from "./pages/NamenszusatzCleanupPage";
import KontaktdatenCleanupPage from "./pages/KontaktdatenCleanupPage";
import ArbeitgeberCleanupPage from "./pages/ArbeitgeberCleanupPage";
import OutlookImportePage from "./pages/OutlookImportePage";
import NurAdmin from "./components/auth/NurAdmin";
import { MailProvider } from "./context/MailContext";
import { NotificationProvider } from "./context/NotificationContext";
import { NavBadgesProvider } from "./context/NavBadgesContext";
import { AuthProvider } from "./context/AuthContext";
import { CommandPaletteProvider } from "./context/CommandPaletteContext";
import { LoginPage } from "./pages/LoginPage";
import { TwoFactorPage } from "./pages/TwoFactorPage";

const client = new ApolloClient({
  link: new HttpLink({ uri: apiUrl("graphql") }),
  cache: new InMemoryCache(),
});


const useStyles = makeStyles({
  container: {
    //display: flex,
  },
});

function AppShell() {
  // Browser-Autofill für die gesamte interne App unterbinden – Formularfelder
  // tragen Kundendaten (Adresse/Bank/…), nicht die des Mitarbeiters.
  useDisableBrowserAutofill();
  return (
    <CommandPaletteProvider>
      <WindowManagerProvider>
        <OfficeOpenMenuProvider>
          {/* Nach-Anruf-Karte (Kontakt, Vorgang, Notiz nach dem Gespräch). Steht
            INNERHALB des WindowManagers, damit „Kontakt anlegen" die volle
            Kontaktanlage und „Neuer Vorgang" den Erstell-Flow als Fenster
            öffnen können. */}
          <NachAnrufKarte />
          <div style={{ display: "flex", minHeight: "100vh" }}>
            <NavigationLayout>
              <Outlet />
            </NavigationLayout>
          </div>
        </OfficeOpenMenuProvider>
      </WindowManagerProvider>
    </CommandPaletteProvider>
  );
}

function RedirectToContractDetail() {
  const { id } = useParams();
  return <Navigate to={`/contracts/${id ?? ""}`} replace />;
}

function RedirectToRiskDetail() {
  const { id } = useParams();
  return <Navigate to={`/risks/${id ?? ""}`} replace />;
}

function AuthCallbackPage() {
  const navigate = useNavigate();

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const authError = params.get("error") || hashParams.get("error");
    const authErrorDescription =
      params.get("error_description") || hashParams.get("error_description");

    if (authError) {
      const message = decodeURIComponent(authErrorDescription || authError);
      sessionStorage.setItem("auth.lastError", message);
      navigate("/", { replace: true });
      return;
    }

    // Better-auth has already exchanged the code and set its session cookie/token
    // by the time we land here. Just bounce to home; AuthContext picks it up.
    navigate("/", { replace: true });
  }, [navigate]);

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      Anmeldung wird abgeschlossen...
    </div>
  );
}

/**
 * Telefonie-Widgets nur innerhalb von KMVS — nicht auf den öffentlichen
 * Kundenseiten (`/public/*`, `/schaden-melden`). Öffnet ein angemeldeter
 * Mitarbeiter einen Kundenlink, sieht er ihn so, wie der Kunde ihn sieht.
 */
function NurIntern({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  if (pathname.startsWith("/public/") || pathname.startsWith("/schaden-melden")) return null;
  return <>{children}</>;
}

function App() {
  const classes = useStyles();

  return (
    <BrowserRouter>
      <AuthProvider>
      {/* Ausdrücklich derselbe Renderer, den auch ein Fensterinhalt im
          Hauptfenster bekommt (BrowserFensterHuelle) — sonst stünden dessen
          Styles doppelt im Dokument. */}
      <RendererProvider renderer={hauptRenderer()} targetDocument={document}>
      <FluentProvider theme={webLightTheme}>
        <TelephonyProvider>
          <RealtimeProvider>
          <ReferenceDataProvider>
            <AppSettingsProvider>
              <MailProvider>
                <NotificationProvider>
                <NavBadgesProvider>
                <NurIntern>
                  <CallWidget />
                  <ActiveCallBar />
                  <SchnellwahlModal />
                </NurIntern>
                <ChatSocketProvider>
                <ChatProvider>
                <ApolloProvider client={client}>
                <Routes>
                  {/*
                    Vorschau des Inhaltsverzeichnisses — Entwurf zur Abnahme
                    (E13). Existiert AUSSCHLIESSLICH im Vite-Dev-Modus: im
                    Produktions-Build ist `import.meta.env.DEV` statisch `false`,
                    der Zweig faellt weg und mit ihm der dynamische Import — die
                    Seite landet also nicht im `dist`-Output.
                  */}
                  {import.meta.env.DEV && VorschauInhaltsverzeichnis && (
                    <Route
                      path="/__vorschau/inhaltsverzeichnis"
                      element={
                        <React.Suspense fallback={null}>
                          <VorschauInhaltsverzeichnis />
                        </React.Suspense>
                      }
                    />
                  )}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/two-factor" element={<TwoFactorPage />} />
                  <Route path="/auth/callback" element={<AuthCallbackPage />} />
                  {/* Seit 11.10.2026 ist die große Schadenmeldung DIE Schadenmeldung;
                      die alte -big-Adresse leitet für verschickte Links weiter. */}
                  <Route path="/schaden-melden" element={<PublicSchadenmeldungBig />} />
                  <Route path="/schaden-melden-big" element={<Navigate to="/schaden-melden" replace />} />
                  <Route
                    path="/public/customer-form/:token"
                    element={<PublicCustomerForm />}
                  />
                  <Route
                    path="/public/customer-form-hub/:token"
                    element={<PublicCustomerFormHub />}
                  />
                  <Route
                    path="/public/customer-check-address-form/:token"
                    element={<PublicCustomerCheckAddressForm />}
                  />
                  <Route
                    path="/public/customer-risk-analysis-form/:token"
                    element={<PublicCustomerRisikoanalyseForm />}
                  />
                  <Route
                    path="/public/customer-risk-analysis-kurz/:token"
                    element={<PublicCustomerRisikoanalyseKurzForm />}
                  />
                  <Route
                    path="/public/customer-risk-analysis-kurz-scroll/:token"
                    element={<PublicCustomerRisikoanalyseKurzScrollForm />}
                  />
                  <Route
                    path="/public/customer-risk-kfz-form/:token"
                    element={<RisikoKfzStepFormPublic />}
                  />
                  <Route
                    path="/public/customer-risk-kennzahlen-form/:token"
                    element={<RisikoKennzahlenStepFormPublic />}
                  />
                  <Route
                    path="/public/customer-risk-immobilie-form/:token"
                    element={<RisikoImmobilieStepFormPublic />}
                  />
                  <Route
                    path="/public/customer-risk-analysis-detail-form/:token"
                    element={<RisikoanalyseDetailFormPublic />}
                  />
                  <Route path="/public/vollmacht/:token" element={<PublicVollmachtUnterschreiben />} />
                  {/*
                    Office-Editor auf voller Flaeche. Steht bewusst VOR
                    <AppShell />, damit weder Navigationsleiste noch Kopfzeile
                    mitkommen — Word im Browser bringt seine eigene
                    Werkzeugleiste mit und braucht den Platz.
                  */}
                  <Route
                    path="/dokumente/:id/bearbeiten"
                    element={<RequireAuth><OfficeEditorPage /></RequireAuth>}
                  />
                  {/*
                    PDF im eigenen Tab („In neuem Tab“ in PdfDocumentView). Ebenfalls
                    VOR <AppShell />: nur das PDF, ohne Navigationsleiste und
                    Kopfzeile.
                  */}
                  <Route
                    path="/dokumente/:id/pdf"
                    element={<RequireAuth><PdfDokumentPage /></RequireAuth>}
                  />
                  <Route element={<AppShell />}>
                    <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
                    <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
                    <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
                    <Route path="/profil" element={<RequireAuth><ProfilePage /></RequireAuth>} />
                    <Route path="/settings/import" element={<RequireAuth><ImportPage /></RequireAuth>} />
                    <Route path="/datenbereinigung" element={<RequireAuth requiredPermission="contacts:read"><DatenbereinigungPage /></RequireAuth>} />
                    <Route path="/datenbereinigung/ansprechpartner" element={<RequireAuth requiredPermission="contacts:read"><AnsprechpartnerCleanupPage /></RequireAuth>} />
                    <Route path="/datenbereinigung/haushalte" element={<RequireAuth requiredPermission="contacts:read"><HaushalteCleanupPage /></RequireAuth>} />
                    <Route path="/datenbereinigung/arbeitgeber" element={<RequireAuth requiredPermission="contacts:update"><ArbeitgeberCleanupPage /></RequireAuth>} />
                    <Route path="/datenbereinigung/altkunden" element={<RequireAuth requiredPermission="contacts:update"><AltkundenCleanupPage /></RequireAuth>} />
                    <Route path="/datenbereinigung/unternehmensstrukturen" element={<RequireAuth requiredPermission="contacts:read"><UnternehmensstrukturenCleanupPage /></RequireAuth>} />
                    <Route path="/datenbereinigung/namenszusatz" element={<RequireAuth requiredPermission="contacts:read"><NamenszusatzCleanupPage /></RequireAuth>} />
                    <Route path="/datenbereinigung/kontaktdaten" element={<RequireAuth requiredPermission="contacts:read"><NurAdmin><KontaktdatenCleanupPage /></NurAdmin></RequireAuth>} />
                    <Route path="/datenbereinigung/outlook-importe" element={<RequireAuth requiredPermission="contacts:read"><OutlookImportePage /></RequireAuth>} />
                    <Route path="/partnerportfolio" element={<RequireAuth adminOnly><PartnerPortfolioUebersicht /></RequireAuth>} />
                    <Route path="/contacts/duplicates" element={<RequireAuth><ContactDuplicatesPage /></RequireAuth>} />
                    <Route path="/kontakte/papierkorb" element={<RequireAuth><PapierkorbPage /></RequireAuth>} />
                    <Route path="/contacts/merge" element={<RequireAuth><ContactMergePage /></RequireAuth>} />
                    <Route path="/contactgroups/merge" element={<RequireAuth requiredPermission="contacts:update"><KundenverbindungMergePage /></RequireAuth>} />
                    {/* Die `key`s sind PFLICHT: Beide Routen rendern dieselbe
                        Komponente an derselben Stelle im Baum. Ohne sie behält
                        React die Instanz beim Wechsel — samt Suche, Filtern und
                        vor allem der ANGEHAKTEN ZEILEN. Ein Klick auf „Löschen"
                        in der einen Liste träfe dann Kontakte der anderen. */}
                    <Route path="/contacts" element={<RequireAuth requiredPermission="contacts:read"><ContactTableRedesign key="kontakte" /></RequireAuth>} />
                    {/* „CO intern": dieselbe Tabelle, andere Variante (PO 09.09.2026). */}
                    <Route path="/co-intern" element={<RequireAuth requiredPermission="cointern:read"><ContactTableRedesign key="cointern" variante="cointern" /></RequireAuth>} />
                    <Route path="/contracts" element={<RequireAuth requiredPermission="contracts:read"><ContractTable /></RequireAuth>} />
                    <Route path="/vertraege" element={<RequireAuth requiredPermission="contracts:read"><VertraegeTabelleRedesign /></RequireAuth>} />
                    <Route path="/risks" element={<RequireAuth requiredPermission="risks:read"><RisikenTabelleDataGrid /></RequireAuth>} />
                    <Route path="/risiken" element={<RequireAuth requiredPermission="risks:read"><RisikenTabelleRedesign /></RequireAuth>} />
                    <Route path="/risks/new" element={<RequireAuth requiredPermission="risks:create"><NewRiskRoute /></RequireAuth>} />
                    <Route path="/risks/new/kfz-old" element={<RequireAuth requiredPermission="risks:create"><RisikoKfzLegacyPage /></RequireAuth>} />
                    <Route path="/risks/new/kennzahlen-old" element={<RequireAuth requiredPermission="risks:create"><RisikoKennzahlenLegacyPage /></RequireAuth>} />
                    <Route path="/contacts/:contactId/kennzahlen/new" element={<RequireAuth requiredPermission="risks:create"><KennzahlenSectionStepForm /></RequireAuth>} />
                    <Route path="/risks/new/immobilie-old" element={<RequireAuth requiredPermission="risks:create"><RisikoImmobilieLegacyPage /></RequireAuth>} />
                    <Route path="/damages" element={<RequireAuth requiredPermission="damages:read"><DamagesTable /></RequireAuth>} />
                    <Route path="/alte-kundenakten" element={<RequireAuth><AlteKundenaktenSeite /></RequireAuth>} />
                    <Route path="/alte-kundenakten/:id" element={<RequireAuth><AlteKundenaktenDetail /></RequireAuth>} />
                    {/* Alte Mappe nur noch bis zum Abschluss von Stufe 3 erreichbar (entfällt in Task 14). */}
                    <Route path="/schaeden/alt/:id" element={<RequireAuth requiredPermission="damages:read"><Schadenmappe /></RequireAuth>} />
                    <Route path="/schaeden/neu" element={<RequireAuth requiredPermission="damages:create"><SchadenmappePage neu /></RequireAuth>} />
                    <Route path="/schaeden/:id/:bereich?/:schritt?" element={<RequireAuth requiredPermission="damages:read"><SchadenmappePage /></RequireAuth>} />
                    <Route path="/zahlungsverkehr" element={<RequireAuth requiredPermission="invoices:read"><RechnungenTabelle /></RequireAuth>} />
                    <Route path="/rechnungen/:id" element={<RequireAuth requiredPermission="invoices:read"><RechnungDetailPage /></RequireAuth>} />
                    <Route
                      path="/contacts/company"
                      element={<RequireAuth requiredPermission="contacts:read"><CompanyContactTable /></RequireAuth>}
                    />
                    <Route
                      path="/contacts/new"
                      element={<RequireAuth requiredPermission="contacts:create"><NewContactPage /></RequireAuth>}
                    />
                    <Route
                      path="/contacts/new/company"
                      element={<RequireAuth requiredPermission="contacts:create"><NewCompanyContactPage /></RequireAuth>}
                    />
                    <Route
                      path="/contacts/new/private"
                      element={<RequireAuth requiredPermission="contacts:create"><NewPrivateContactPage /></RequireAuth>}
                    />
                    <Route
                      path="/contacts/:contactId/risikoanalyse/:token"
                      element={<RequireAuth requiredPermission="contacts:read"><InternalRisikoanalyseFormPage /></RequireAuth>}
                    />
                    <Route
                      path="/contacts/:id/convert/:target"
                      element={<RequireAuth requiredPermission="contacts:update"><ConvertContactPage /></RequireAuth>}
                    />
                    <Route
                      path="/contacts/:id"
                      element={<RequireAuth requiredPermission="contacts:read"><ContactDetailsRedesign /></RequireAuth>}
                    />
                    <Route
                      path="/contacts/:id/auswertung/:token"
                      element={<RequireAuth requiredPermission="contacts:read"><AnalysemodusAuswertungRedirect /></RequireAuth>}
                    />
                    <Route
                      path="/contacts/:id/auswertung"
                      element={<RequireAuth requiredPermission="contacts:read"><AnalysemodusAuswertungRedirect /></RequireAuth>}
                    />
                    <Route
                      path="/contacts/:contactId/beratermodus"
                      element={<RequireAuth requiredPermission="contacts:read"><BeratermodusFormPage /></RequireAuth>}
                    />
                    <Route
                      path="/contacts/:contactId/beratermodus/:token"
                      element={<RequireAuth requiredPermission="contacts:read"><BeratermodusFormPage /></RequireAuth>}
                    />
                    <Route
                      path="/contacts/:contactId/risikoanalyse-step"
                      element={<RequireAuth requiredPermission="contacts:read"><RisikoanalyseStepInternalPage /></RequireAuth>}
                    />
                    <Route
                      path="/contacts/:contactId/risikoanalyse-step/:token"
                      element={<RequireAuth requiredPermission="contacts:read"><RisikoanalyseStepInternalPage /></RequireAuth>}
                    />
                    <Route
                      path="/kontakte/edit/:id"
                      element={<RequireAuth requiredPermission="contacts:update"><ContactDetailsRedesign /></RequireAuth>}
                    />
                    {/* Alte Edit-Routen leiten auf die neuen Detail-Views weiter
                        (deep-link compatibility). */}
                    <Route path="/vertraege/edit/:id" element={<RedirectToContractDetail />} />
                    <Route path="/risiken/edit/:id" element={<RedirectToRiskDetail />} />
                    <Route path="/schaeden/edit/:id" element={<RequireAuth requiredPermission="damages:update"><DamagesForm /></RequireAuth>} />
                    <Route path="/kunde/new" element={<RequireAuth requiredPermission="contacts:create"><ContactDetailsForm /></RequireAuth>} />
                    <Route path="/vertrag/new" element={<RequireAuth requiredPermission="contracts:create"><NewContractPage /></RequireAuth>} />
                    <Route path="/vertrag/transport" element={<RequireAuth requiredPermission="contracts:create"><TransportContractCreatePage /></RequireAuth>} />
                    <Route path="/vertrag/ertragsausfall/new" element={<RequireAuth requiredPermission="contracts:create"><ErtragsausfallContractCreatePage /></RequireAuth>} />
                    <Route path="/risiko/new" element={<RequireAuth requiredPermission="risks:create"><RisikoForm /></RequireAuth>} />
                    <Route path="/risiko/immo/new" element={<RequireAuth requiredPermission="risks:create"><RisikoImmoForm /></RequireAuth>} />
                    <Route path="/risks/:riskObjectId/contracts/new" element={<RequireAuth requiredPermission="contracts:create"><ImmobilieContractCreatePage /></RequireAuth>} />
                    <Route path="/risks/:riskObjectId/contracts/new-kfz" element={<RequireAuth requiredPermission="contracts:create"><KfzContractCreatePage /></RequireAuth>} />
                    <Route path="/contracts/new-kfz" element={<RequireAuth requiredPermission="contracts:create"><KfzContractCreatePage /></RequireAuth>} />
                    <Route path="/risks/:id" element={<RequireAuth requiredPermission="risks:read"><RiskDetailDispatcher /></RequireAuth>} />
                    <Route path="/contracts/:id" element={<RequireAuth requiredPermission="contracts:read"><ContractDetailDispatcher /></RequireAuth>} />
                    <Route path="/damages/new" element={<RequireAuth requiredPermission="damages:create"><SchadenForm /></RequireAuth>} />

                    <Route path="/termine" element={<RequireAuth><CalendarView /></RequireAuth>} />
                    {/* Termine-SP7 (A12): Termin-Notizen als eigene Seite im OneNote-Stil. */}
                    <Route path="/termine/notizen/:terminId" element={<RequireAuth><TerminNotizenPage /></RequireAuth>} />
                    <Route path="/emails" element={<RequireAuth><SchriftverkehrAnsicht /></RequireAuth>} />
                    <Route path="/anrufe" element={<RequireAuth requiredPermission="calls:read"><TelephonyPage /></RequireAuth>} />
                    {/* Deep-Link auf den Mailbox-Tab (alte /anrufe/mailbox-Route). */}
                    <Route path="/anrufe/mailbox" element={<Navigate to="/anrufe?tab=mailbox" replace />} />
                    <Route path="/chat" element={<RequireAuth><ChatPage /></RequireAuth>} />
                    <Route path="/chat/:conversationId" element={<RequireAuth><ChatPage /></RequireAuth>} />
                    <Route path="/einstellungen/telefonie" element={<RequireAuth><TelephonyExtensionsPage /></RequireAuth>} />
                    <Route path="/einstellungen/tickets" element={<RequireAuth><TicketsPage /></RequireAuth>} />
                    <Route path="/einstellungen/tickets/:id" element={<RequireAuth><TicketsPage /></RequireAuth>} />
                    <Route path="/vorgaenge" element={<RequireAuth requiredPermission="vorgaenge:read"><VorgaengeMo /></RequireAuth>} />
                    {/* `/vorgaenge/mails` — „Mails zu meinen Vorgängen" — ist am
                        21.09.2026 entfallen (PO), Seite und Route mit. Den
                        Schriftverkehr zeigt der Verlauf im Vorgang selbst. */}
                    {/* Es gibt EINE Vorgangsansicht: die Notizansicht V4 (normale Ansicht
                        seit dem 14.09.2026). Die Altansicht V2 und die verworfenen Entwürfe
                        V3, V5 und V6 sind seit dem 20.09.2026 entfernt (PO: „die anderen
                        Versionen vorher rausnehmen, das ist sonst confusend").

                        `/vorgaenge/v4/:id` bleibt als zweiter Weg zur selben Seite: der Pfad
                        steht in Lesezeichen und in verschickten Links.

                        `/vorgaenge/v2|v3|v5|v6/:id` leiten auf `/vorgaenge/:id` weiter — aus
                        demselben Grund: ein Lesezeichen soll beim Vorgang landen, nicht auf
                        „Seite nicht gefunden".

                        Zwei statische Segmente ranken in React Router höher als das dynamische
                        ":id" darunter — die Reihenfolge ist trotzdem so gewählt, dass die
                        Absicht beim Lesen sichtbar bleibt. */}
                    <Route path="/vorgaenge/v6/:id" element={<RequireAuth requiredPermission="vorgaenge:read"><VorgangAltpfadRedirect /></RequireAuth>} />
                    <Route path="/vorgaenge/v5/:id" element={<RequireAuth requiredPermission="vorgaenge:read"><VorgangAltpfadRedirect /></RequireAuth>} />
                    <Route path="/vorgaenge/v4/:id" element={<RequireAuth requiredPermission="vorgaenge:read"><VorgangDetailPageV4 /></RequireAuth>} />
                    <Route path="/vorgaenge/v3/:id" element={<RequireAuth requiredPermission="vorgaenge:read"><VorgangAltpfadRedirect /></RequireAuth>} />
                    <Route path="/vorgaenge/v2/:id" element={<RequireAuth requiredPermission="vorgaenge:read"><VorgangAltpfadRedirect /></RequireAuth>} />
                    <Route path="/vorgaenge/:id" element={<RequireAuth requiredPermission="vorgaenge:read"><VorgangDetailPageV4 /></RequireAuth>} />
                    {/* To-dos haben keine Detailseite mehr — die Route bleibt als
                        Weiterleitung auf den Eltern-Vorgang bestehen, weil `/todos/:id`
                        in Benachrichtigungen und in bereits zugestellten Web-Push-
                        Nachrichten als Deep-Link persistiert ist. */}
                    <Route path="/todos/:id" element={<RequireAuth requiredPermission="vorgaenge:read"><TodoRedirectPage /></RequireAuth>} />
                    <Route path="/access-denied" element={<AccessDeniedPage />} />
                    <Route path="/pending-approval" element={<PendingApprovalPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                </Routes>
              </ApolloProvider>
              </ChatProvider>
              </ChatSocketProvider>
                </NavBadgesProvider>
                </NotificationProvider>
              </MailProvider>
            </AppSettingsProvider>
          </ReferenceDataProvider>
          </RealtimeProvider>
        </TelephonyProvider>
      </FluentProvider>
      </RendererProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
